import axios from 'axios';
import env from '@config/env';
import { logger } from '@utils/logger';
import { localStorage } from '@utils/storage';
import { STORAGE_KEYS, HTTP_STATUS } from '@constants';

const pendingRequests = new Map();
const requestCache = new Map();
const CACHE_DURATION = 5000;

const generateRequestKey = (config) => {
  const { method, url, params, data } = config;
  return `${method}:${url}:${JSON.stringify(params)}:${JSON.stringify(data)}`;
};

const baseInstance = axios.create({
  baseURL: env.api.baseUrl,
  timeout: env.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

const axiosInstance = new Proxy(baseInstance, {
  get(target, prop) {
    if (['get', 'delete', 'head', 'options'].includes(prop)) {
      return (...args) => {
        const config = args[1] || {};
        const url = args[0];
        const fullConfig = { ...config, method: prop, url };
        const requestKey = generateRequestKey(fullConfig);
        
        const cachedResponse = requestCache.get(requestKey);
        if (cachedResponse && Date.now() - cachedResponse.timestamp < CACHE_DURATION) {
          // logger.debug('API Request (cached):', {
          //   method: prop.toUpperCase(),
          //   url: url,
          // });
          return Promise.resolve(cachedResponse.response);
        }
        
        if (pendingRequests.has(requestKey)) {
          // logger.debug('API Request (deduped):', {
          //   method: prop.toUpperCase(),
          //   url: url,
          // });
          return pendingRequests.get(requestKey);
        }
        
        const requestPromise = target[prop](...args)
          .then(response => {
            requestCache.set(requestKey, {
              response: response,
              timestamp: Date.now()
            });
            pendingRequests.delete(requestKey);
            return response;
          })
          .catch(error => {
            pendingRequests.delete(requestKey);
            throw error;
          });
        
        pendingRequests.set(requestKey, requestPromise);
        return requestPromise;
      };
    }
    return target[prop];
  }
});

baseInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.get(STORAGE_KEYS.ACCESS_TOKEN);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // logger.debug('API Request:', {
    //   method: config.method?.toUpperCase(),
    //   url: config.url,
    //   data: config.data,
    //   params: config.params,
    // });

    return config;
  },
  (error) => {
    logger.error('Request Interceptor Error:', error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    const requestKey = generateRequestKey(response.config);
    
    if (response.config.method?.toLowerCase() === 'get') {
      requestCache.set(requestKey, {
        response: response,
        timestamp: Date.now()
      });
      
      pendingRequests.delete(requestKey);
    }

    // logger.debug('API Response:', {
    //   status: response.status,
    //   url: response.config.url,
    //   data: response.data,
    // });

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    logger.error('API Error:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      data: error.response?.data,
    });

    // Handle subscription expired or no active subscription errors
    const errorCode = error.response?.data?.error;
    if (errorCode === 'SUBSCRIPTION_EXPIRED' || errorCode === 'NO_ACTIVE_SUBSCRIPTION') {
      // Store error details for components to access
      if (error.response?.data?.data) {
        window.dispatchEvent(new CustomEvent('subscription-error', {
          detail: {
            error: errorCode,
            message: error.response.data.message,
            data: error.response.data.data
          }
        }));
      }
    }

    // Skip token refresh for auth endpoints (login, register, etc.)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || 
                          originalRequest.url?.includes('/auth/register') ||
                          originalRequest.url?.includes('/auth/refresh');

    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.get(STORAGE_KEYS.REFRESH_TOKEN);
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post(`${env.api.baseUrl}/auth/refresh`, {
          refreshToken,
        });

        // Handle wrapped response from backend
        const responseData = response.data?.data || response.data;
        const { accessToken } = responseData;
        localStorage.set(STORAGE_KEYS.ACCESS_TOKEN, accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        localStorage.remove(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.remove(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.remove(STORAGE_KEYS.USER);
        
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
